/**
 * Detecta o ambiente de execução para filtrar ruído de logs.
 *
 * Combina `VERCEL_ENV` (server-side) com hostname check (client-side).
 * Importante: tratamos `localhost`, `127.0.0.1`, IPs LAN (192.168.x.x) e
 * `*.local` como development. Preview deploys (`*.vercel.app`) viram preview.
 *
 * Estratégia de persistência:
 *  - production: tudo persiste
 *  - preview:    tudo exceto debug
 *  - development: só critical (overridable via flag manual)
 */
export type LogEnvironment = 'production' | 'preview' | 'development';

export function getLogEnvironment(): LogEnvironment {
  // Server-side: usa env vars do Vercel
  if (typeof window === 'undefined') {
    const vercelEnv = process.env.VERCEL_ENV;
    if (vercelEnv === 'production') return 'production';
    if (vercelEnv === 'preview') return 'preview';
    // Sem VERCEL_ENV definido = local dev
    if (process.env.NODE_ENV === 'production') return 'production';
    return 'development';
  }

  // Client-side: hostname
  const host = window.location.hostname.toLowerCase();
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '0.0.0.0' ||
    host.startsWith('192.168.') ||
    host.startsWith('10.') ||
    host.startsWith('172.16.') ||
    host.endsWith('.local')
  ) {
    return 'development';
  }
  if (host.endsWith('.vercel.app')) return 'preview';
  return 'production';
}

/**
 * Decide se um log deve ser persistido na tabela `platform_logs`.
 * Dev só persiste critical (filtra ruído de hot-reload, refactor, etc).
 * Override via env var `LOG_FORCE=true` para debug local quando necessário.
 */
export function shouldPersistLog(level: string, env: LogEnvironment): boolean {
  if (typeof process !== 'undefined' && process.env.LOG_FORCE === 'true') return true;
  if (env === 'production') return true;
  if (env === 'preview') return level !== 'debug';
  // development
  return level === 'critical';
}
